import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/cartpage.scss";

const STANDARD_SHIPPING = Number(siteConfig.shippingRate) || 9.99;

const money = new Intl.NumberFormat(siteConfig.locale || "en-US", {
  style: "currency",
  currency: siteConfig.currency || "USD",
});

const parsePrice = (price) => {
  if (typeof price === "number") return Number.isFinite(price) ? price : 0;
  const parsed = Number(String(price ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getImage = (product) => {
  if (product?.image) return product.image;
  if (Array.isArray(product?.images)) {
    const firstImage = product.images[0];
    return typeof firstImage === "string" ? firstImage : firstImage?.src;
  }
  return "";
};

const getItemDetails = (item, product) => {
  const details = [];
  if (item.size) details.push(["Size", item.size]);
  if (item.color) details.push(["Option", item.color]);

  const selectedAttributes = item.attributes || item.options;
  if (selectedAttributes && typeof selectedAttributes === "object") {
    Object.entries(selectedAttributes).forEach(([label, value]) => {
      if (value != null && value !== "" && !Array.isArray(value)) {
        details.push([label, value]);
      }
    });
  } else if (product) {
    const productDetails = {
      weight: product.weight || product.attributes?.weight,
      serves: product.serves || product.attributes?.serves,
      selection: product.attributes?.selection,
    };
    Object.entries(productDetails).forEach(([key, value]) => {
      if (value != null && value !== "" && !Array.isArray(value)) {
        details.push([key, value]);
      }
    });
  }

  return details.filter(
    ([label, value], index, all) =>
      all.findIndex(
        ([candidateLabel, candidateValue]) =>
          String(candidateLabel).toLowerCase() === String(label).toLowerCase() &&
          String(candidateValue) === String(value)
      ) === index
  );
};

const CartPage = () => {
  const navigate = useNavigate();
  const cart = useContext(CartContext);
  const cartItems = cart?.cartItems || [];

  const getProductById = (id) =>
    productData.find((product) => String(product.id) === String(id));

  const lineItems = cartItems
    .map((item) => {
      const product = getProductById(item.id);
      if (!product) return null;
      const quantity = Math.max(1, Number(item.quantity) || 1);
      return {
        item,
        product,
        quantity,
        lineTotal: parsePrice(product.price) * quantity,
      };
    })
    .filter(Boolean);

  const subtotal = lineItems.reduce((sum, line) => sum + line.lineTotal, 0);
  const shipping = STANDARD_SHIPPING;
  const total = subtotal + shipping;

  const updateItemQuantity = (item, nextQuantity) => {
    if (nextQuantity < 1) return;
    cart.updateQuantity(item.id, item.size, item.color, nextQuantity);
  };

  const removeItem = (item) => {
    cart.removeFromCart(item.id, item.size, item.color);
  };

  const beginCheckout = () => {
    if (!lineItems.length) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/checkout");
  };

  if (!lineItems.length) {
    return (
      <main className="cart-page cart-page--empty">
        <section className="cart-empty" aria-labelledby="empty-cart-title">
          <span className="cart-empty__eyebrow">Your basket</span>
          <h1 id="empty-cart-title">Your cart is ready for something good.</h1>
          <p>
            Discover artisan pantry staples, exceptional cheese, and gifts made
            for sharing from {siteConfig.brandName}.
          </p>
          <Link className="cart-button cart-button--primary" to="/shop">
            Explore the shop
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="cart-page__shell">
        <header className="cart-page__header">
          <div>
            <span className="cart-page__eyebrow">Your basket</span>
            <h1>Shopping cart</h1>
          </div>
          <p>{lineItems.length} curated item{lineItems.length === 1 ? "" : "s"}</p>
        </header>

        <div className="cart-page__layout">
          <section className="cart-list" aria-labelledby="cart-items-title">
            <h2 id="cart-items-title" className="sr-only">
              Cart items
            </h2>

            {lineItems.map(({ item, product, quantity, lineTotal }) => {
              const itemDetails = getItemDetails(item, product);
              const itemKey = `${item.id}-${item.size || "default"}-${
                item.color || "default"
              }`;

              return (
                <article className="cart-line" key={itemKey}>
                  <Link
                    className="cart-line__image-wrap"
                    to={`/shopDetails/${product.id}`}
                    aria-label={`View ${product.name}`}
                  >
                    <img
                      className="cart-line__image"
                      src={getImage(product)}
                      alt={product.name}
                      width="126"
                      height="126"
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>

                  <div className="cart-line__details">
                    <p className="cart-line__category">{product.category}</p>
                    <h3>
                      <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                    </h3>
                    {itemDetails.length > 0 && (
                      <dl className="cart-line__attributes">
                        {itemDetails.map(([label, value]) => (
                          <div key={`${label}-${value}`}>
                            <dt>{label}</dt>
                            <dd>{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    <button
                      className="cart-line__remove"
                      type="button"
                      onClick={() => removeItem(item)}
                      aria-label={`Remove ${product.name} from cart`}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="cart-line__purchase">
                    <span className="cart-line__unit-price">
                      {money.format(parsePrice(product.price))} each
                    </span>
                    <div
                      className="quantity-stepper"
                      role="group"
                      aria-label={`Quantity for ${product.name}`}
                    >
                      <button
                        type="button"
                        onClick={() => updateItemQuantity(item, quantity - 1)}
                        aria-label={`Decrease ${product.name} quantity`}
                        disabled={quantity === 1}
                      >
                        <span aria-hidden="true">−</span>
                      </button>
                      <output aria-live="polite" aria-label="Quantity">
                        {quantity}
                      </output>
                      <button
                        type="button"
                        onClick={() => updateItemQuantity(item, quantity + 1)}
                        aria-label={`Increase ${product.name} quantity`}
                      >
                        <span aria-hidden="true">+</span>
                      </button>
                    </div>
                    <strong>{money.format(lineTotal)}</strong>
                  </div>
                </article>
              );
            })}

            <Link className="cart-page__continue" to="/shop">
              <span aria-hidden="true">←</span> Continue shopping
            </Link>
          </section>

          <aside className="cart-summary" aria-labelledby="cart-summary-title">
            <h2 id="cart-summary-title">Order summary</h2>

            <div className="cart-summary__shipping-note" aria-live="polite">
              <strong>Flat-rate standard shipping</strong>
              <span>{money.format(shipping)} per order.</span>
            </div>

            <dl className="cart-summary__totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{money.format(subtotal)}</dd>
              </div>
              <div>
                <dt>Standard shipping</dt>
                <dd>{money.format(shipping)}</dd>
              </div>
              <div className="cart-summary__grand-total">
                <dt>Total</dt>
                <dd>{money.format(total)}</dd>
              </div>
            </dl>

            <p className="cart-summary__fine-print">
              Discounts can be applied at checkout. Any applicable taxes are
              shown before payment.
            </p>
            <button
              className="cart-button cart-button--primary"
              type="button"
              onClick={beginCheckout}
            >
              Continue to checkout
            </button>
            <div className="cart-summary__assurance">
              <span aria-hidden="true">✓</span>
              <span>Carefully packed for a fresh arrival</span>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default CartPage;
